/**
 * The far end of a home dispatch. The home navigated the moment the canvas
 * existed; the session and the first message were still in flight. This
 * claims that work and opens the running session in the floating chat. When
 * the message never left, the prompt lands in the chat's composer instead,
 * so nothing typed on the home is lost. A quick action's header goes on the
 * canvas first, at the origin, and the dispatcher holds the message until it
 * is there. The chat opens at full height, as the header's expand control
 * leaves it, so the answer is in front of them. When that first turn ends
 * having placed nodes, the view fits them in the part of the canvas left of
 * the panel, once. Ordinary canvas visits have no pending work and this
 * renders nothing.
 */

import { useFlowSessionById } from "@/api/flow_sessions";
import { pushComposerDraft } from "@/composer/composerDraft";
import Sentry from "@/integrations/Sentry";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { emitCanvasFocusNodes } from "../constants/canvasFocusNodes.constants";
import { useCanvasApi, useCanvasDoc } from "../context/CanvasContext";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import { useCanvasRoute } from "../context/CanvasRouteContext";
import {
  useClaimedDispatch,
  type PendingDispatch,
} from "../hooks/canvasDispatch";
import { useEnsureCanvasAgentChatOpen } from "../hooks/useEnsureCanvasAgentChatOpen";
import { placeHtmlBlock } from "../utils/placeHtmlBlock";

export const AGENT_FAILED =
  "The agent didn't start. Your message is in the chat, ready to send.";

/** Nodes land a moment after the session reports the turn done. */
export const FIT_DELAY_MS = 500;
export const FIT_RETRY_MS = 1500;

export function CanvasDispatchArrival() {
  const { canvasId } = useCanvasRoute();
  const pending = useClaimedDispatch(canvasId);
  const { openSession, setSizeClass, bottomToolbarRightClearancePx } =
    useCanvasFloatingChat();
  const ensureChatOpen = useEnsureCanvasAgentChatOpen();
  const queryClient = useQueryClient();
  const canvas = useCanvasApi();
  const { nodes } = useCanvasDoc();
  const handled = useRef<PendingDispatch | null>(null);
  const placed = useRef<PendingDispatch | null>(null);
  // The first turn's session, with the node ids the canvas had before it.
  const [turn, setTurn] = useState<{
    sessionId: string;
    before: Set<string>;
  } | null>(null);
  const { data: session } = useFlowSessionById(turn?.sessionId ?? "", {
    enabled: !!turn,
  });
  const sawProcessing = useRef(false);
  const fitted = useRef(false);
  const latest = useRef({ nodes, inset: bottomToolbarRightClearancePx });
  latest.current = { nodes, inset: bottomToolbarRightClearancePx };

  useEffect(() => {
    const header = pending?.header;
    // The store drops a node added before the room has loaded.
    if (!pending || !header || !canvasId || !canvas.isReady) return;
    if (placed.current === pending) return;
    placed.current = pending;
    void placeHtmlBlock({
      canvasId,
      queryClient,
      canvas,
      file: header.file,
      html: header.html,
      position: { x: 0, y: 0 },
      width: header.width,
      height: header.height,
      chromeless: true,
      source: "home_header",
      // Refused once the message went without it: the agent makes its own.
      admit: () => header.settle(true),
    }).catch((error) => {
      Sentry.captureException(error);
      header.settle(false);
    });
  }, [pending, canvasId, canvas, queryClient]);

  useEffect(() => {
    if (!pending || handled.current === pending) return;
    // Taken before the header lands, so a quick action's header is fitted too.
    const nodesBefore = new Set(latest.current.nodes.map((n) => n.id));
    let live = true;
    void pending.outcome.then((outcome) => {
      if (!live || handled.current === pending) return;
      // Consume before opening the chat: opening it changes ensureChatOpen's
      // identity and reruns this effect, and a settled promise must not
      // replace the session stack a second time.
      handled.current = pending;
      setSizeClass("full-height");
      if (outcome.kind === "sent") {
        setTurn({ sessionId: outcome.sessionId, before: nodesBefore });
        openSession(outcome.sessionId, "replace");
        return;
      }
      toast.error(AGENT_FAILED);
      if (outcome.kind === "post-failed") {
        pushComposerDraft(queryClient, outcome.sessionId, pending.prompt);
        openSession(outcome.sessionId, "replace");
      } else {
        void ensureChatOpen(pending.prompt);
      }
    });
    return () => {
      live = false;
    };
  }, [pending, openSession, setSizeClass, ensureChatOpen, queryClient]);

  // Once the turn has run and ended, fit what it placed left of the chat.
  // The timers live outside the effect: the session updates again after the
  // turn (its name, its read state) and a cleanup on each would cancel them.
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  useEffect(() => {
    if (!turn || !session || fitted.current) return;
    if (session.status === "processing") {
      sawProcessing.current = true;
      return;
    }
    if (!sawProcessing.current) return;
    fitted.current = true;
    const fit = () => {
      const { nodes: current, inset } = latest.current;
      const added = current
        .map((n) => n.id)
        .filter((id) => !turn.before.has(id));
      return (
        added.length > 0 &&
        emitCanvasFocusNodes(added, { insetRight: inset, select: false })
      );
    };
    const retry = setTimeout(fit, FIT_DELAY_MS + FIT_RETRY_MS);
    const first = setTimeout(() => {
      if (fit()) clearTimeout(retry);
    }, FIT_DELAY_MS);
    timers.current = [first, retry];
  }, [turn, session]);

  return null;
}
